import {html} from "lit";
import {container} from "tsyringe";
import {LockConflictError} from "../../connectivity/lock-conflict.error";
import {UiHelper} from "../../helpers/ui.helper";
import type {Linkview} from "../entities/linkview.entity";
import {formatHolderName} from "./format-holder-name.helper";
import {AbstractLinkviewRepository, LINKVIEW_REPOSITORY} from "../repositories/abstract-linkview.repository";
import {UserRepository} from "../repositories/user.repository";

/**
 * The "Bearbeiten" click's whole flow for a Linkboard (Anke, 2026-09-17 —
 * "die Linkboards auf den LOCK-mechanismus umstellen, wie die
 * Whiteboards"): acquire the edit lock, or — when somebody else already
 * holds it — warn, name them, and take it over on confirmation.
 *
 * Extracted from `linkview-sidebar-list.component.ts`'s own (now-removed)
 * `_onToggleLock`/`_confirmAndOverride`/`_runLockCall` — this is the exact
 * same three-state holder logic (free / held by me / held by somebody
 * else), just narrowed to the ACQUIRE half: a dedicated "Bearbeiten" button
 * only ever enters edit mode, it no longer doubles as a release toggle —
 * `o2d-linkview.view.ts`'s own "Speichern"/"Abbrechen" toolbar buttons are
 * the two ways out now, mirroring `acquire-knowledge-object-edit-lock.helper
 * .ts` (which itself was built to mirror THIS flow — the two are siblings,
 * not one derived from the other).
 *
 * `entity.lockedBy` IS THE SOURCE OF TRUTH FOR THE HOLDER NAME SHOWN, and it
 * is always CURRENT by the time this reads it: `BackendLinkviewRepository`
 * refreshes the (identity-cached) `items$` row's lock fields BEFORE a
 * `LockConflictError` rejection settles.
 *
 * EXACTLY ONE RETRY, same bound as the original sidebar flow: a second
 * conflict in the same round (somebody raced the take-over between the
 * confirm dialog and the override command reaching the server) means the
 * board is genuinely contended right now, and looping again would advance
 * unattended.
 *
 * @returns `true` once the lock is held by the acting user (acquired
 *          directly, or taken over on confirmation); `false` if the user
 *          declined to take over, or a non-conflict error occurred (already
 *          notified).
 */
export async function acquireLinkviewEditLock(
    item: Linkview,
    isRetry = false,
): Promise<boolean> {
    const repository = container.resolve(LINKVIEW_REPOSITORY);

    if (item.lockedBy === null) {
        return runLockCall(repository.acquireLock(item.uid), repository, item, isRetry);
    }

    if (item.locked === false) {
        // Already held by ME (e.g. a previous session that never
        // saved/released) — re-acquiring is a documented no-op, but still
        // goes through the same call so a stale server-side state (taken
        // over elsewhere in the meantime) surfaces as a real conflict
        // instead of silently claiming success.
        return runLockCall(repository.acquireLock(item.uid), repository, item, isRetry);
    }

    return confirmAndOverride(item, repository, isRetry);
}

/**
 * Resolves the holder's name, warns, and — only on an explicit confirmation
 * — takes the lock over. A holder whose row cannot be resolved is still
 * warned about, named "Unbekannt" (via `formatHolderName`): refusing to warn
 * would make a lock abandoned by a deleted user permanently un-take-over-able.
 */
async function confirmAndOverride(
    item: Linkview,
    repository: AbstractLinkviewRepository,
    isRetry: boolean,
): Promise<boolean> {
    const holder = await container.resolve(UserRepository).findOneById(item.lockedBy!)
        .catch(() => null);
    const holderName = formatHolderName(holder);

    const confirmed = await UiHelper.confirm({
        title: 'Linkboard wird bearbeitet',
        content: html`<p>${holderName} bearbeitet dieses Linkboard gerade.
            Wenn Sie fortfahren, wird die Bearbeitung übernommen und
            ${holderName} kann nicht mehr speichern.</p>`,
        confirmLabel: 'Übernehmen',
        cancelLabel: 'Abbrechen',
    });

    if (!confirmed) {
        return false;
    }

    // The expected holder goes out VERBATIM, in the transport spelling
    // the row carried it in — see `overrideLock`'s own doc comment for why
    // converting it breaks every take-over.
    return runLockCall(repository.overrideLock(item.uid, item.lockedBy), repository, item, isRetry);
}

/**
 * One rejection policy for every lock call this flow makes.
 *
 * A `LockConflictError` means somebody else got there first and NOTHING was
 * written. The repository has already refreshed `items$`'s row before the
 * rejection settled, so the current holder is there by now — re-enter the
 * TOP-LEVEL function against that fresh row (not straight back into a
 * second confirm dialog): if the board is free now, it re-acquires outright
 * with no dialog at all.
 */
async function runLockCall(
    call: Promise<void>,
    repository: AbstractLinkviewRepository,
    item: Linkview,
    isRetry: boolean,
): Promise<boolean> {
    try {
        await call;
        return true;
    } catch (error) {
        if (!(error instanceof LockConflictError)) {
            notifyLockFailed();
            return false;
        }

        if (isRetry) {
            // The retry round is the last one — see this module's own doc
            // comment for why looping further would advance unattended.
            notifyLockFailed();
            return false;
        }

        const refreshed = repository.items$.value.find((candidate) => candidate.uid === item.uid);
        if (!refreshed) {
            notifyLockFailed();
            return false;
        }

        return acquireLinkviewEditLock(refreshed, true);
    }
}

function notifyLockFailed(): void {
    UiHelper.notify({
        level: 'error',
        title: 'Fehler',
        content: html`<p>Die Bearbeitung konnte nicht gestartet werden.</p>`,
        closable: true,
    });
}
